<template>
  <tr>
    <td>
      <label class="form-switch m-0 pl-0">
        <input type="checkbox" :checked="selected" @change="toggle">
        <i class="form-icon"></i>
      </label>
    </td>
    <td>
      {{ professional.name }}
      <br>
      <small v-if="professional.specialty" class="text-gray">
        {{ professional.specialty.name }}
      </small>

      <div
        class="mt-1"
        :class="alert.type === 'error' ? 'text-error' : 'text-warning'"
        v-if="hasAlert"
      >
        <span class="text-large mr-1">
          <fa-icon :icon="['far', 'exclamation-triangle']" />
        </span>
        <strong>{{ alert.message }}</strong>
      </div>
    </td>
    <td>
      <span
        class="text-large d-inline-block pt-1"
        :class="{ 'text-gray': !selected }"
      >
        <fa-icon :icon="['fal', 'arrow-right']" />
      </span>
    </td>
    <td>
      <select
        class="form-select"
        v-model="form.mappingId"
        :disabled="!canEdit"
        :class="{ readonly: selected && !!form.mappingId }"
        @change="onMxmedProfessionalChange"
      >
        <option value="">[Selecione]</option>
        <option
          v-for="item in mxmedProfessionals"
          :key="item.id"
          :value="String(item.id)"
          :disabled="!item.active"
        >
          {{ item.name }}
          <template v-if="!item.active"> (inativo)</template>
        </option>
      </select>

      <template v-if="form.mappingId">
        <div class="mt-2">
          <button
            class="btn btn-sm btn-info-outline"
            :class="{ loading: loadingProcedures }"
            :disabled="!canEdit"
            @click="openResourcesModal('procedure')"
          >
            Procedimentos ({{ form.procedures.length }})
          </button>
          <button
            class="btn btn-sm btn-info-outline ml-2"
            :disabled="!canEdit"
            @click="openResourcesModal('insurance')"
          >
            Convênios ({{ form.insurances.length }})
          </button>
        </div>
      </template>
    </td>
    <td>
      <button
        class="btn btn-action btn-icon tooltip"
        :class="syncButtonClasses"
        data-tooltip="Sincronizar agenda"
        :disabled="!canSync"
        @click="sync"
      >
        <fa-icon v-if="form.synced" :icon="['fal', 'check']" />
        <fa-icon v-else :icon="['fal', 'sync']" />
      </button>
      <button
        class="btn btn-action btn-primary btn-icon tooltip ml-1"
        :class="{ loading: saving }"
        data-tooltip="Salvar"
        :disabled="!dirty || !canSave"
        @click="save"
      >
        <fa-icon :icon="['fal', 'save']" />
      </button>
    </td>

    <professional-item-resources
      v-if="resourceModal.show"
      :show.sync="resourceModal.show"
      :type="resourceModal.type"
      :professional-id="professional.id"
      :mxmed-professional-id="form.mappingId"
      :procedures="form.procedures"
      :insurances="form.insurances"
      :mxmed-insurances="mxmedInsurances"
      @save="onResourcesSave"
    ></professional-item-resources>
  </tr>
</template>

<script>
import { mapState } from 'vuex';
import AblyService from '@/services/ably';
import ProfessionalItemResources from './ProfessionalItemResources.vue';
import api from '../api';

export default {
  components: {
    ProfessionalItemResources,
  },
  props: {
    professional: {
      type: Object,
      default: () => ({}),
    },
    mappings: {
      type: Object,
      default: null,
    },
    mxmedProfessionals: {
      type: Array,
      default: () => [],
    },
    mxmedInsurances: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      saving: false,
      syncing: false,
      selected: false,
      dirty: false,
      loadingProcedures: false,
      form: this.blankForm(),
      resourceModal: {
        show: false,
        type: '',
      },
      alert: {
        type: '',
        message: '',
      },
      ablyUnsubscribe: null,
    };
  },
  mounted() {
    this.init();
  },
  beforeDestroy() {
    if (this.ablyUnsubscribe) {
      this.ablyUnsubscribe();
    }
  },
  computed: {
    ...mapState({
      user: ({ auth }) => auth.user,
    }),
    canEdit() {
      return this.selected && !this.saving && !this.syncing;
    },
    canSave() {
      return !this.saving && !this.syncing;
    },
    canSync() {
      return this.selected
        && !!this.form.mappingId
        && this.form.procedures.length > 0
        && !this.syncing
        && !this.form.synced;
    },
    eventsChannel() {
      return `${this.user.branch.id}-mxmed-sync`;
    },
    syncButtonClasses() {
      if (this.form.synced) {
        return 'btn-success-outline';
      }
      return {
        loading: this.syncing,
        'btn-info-outline': this.canSync,
        'btn-gray-outline': !this.canSync,
      };
    },
    hasAlert() {
      return this.selected && this.alert.type && this.alert.message;
    },
  },
  methods: {
    blankForm() {
      return {
        id: this.professional.id,
        mappingId: '',
        procedures: [],
        insurances: [],
        synced: false,
      };
    },
    init() {
      const found = this.mappings && this.mappings.professionals
        ? this.mappings.professionals.find(({ id }) => id === this.professional.id)
        : null;

      if (found) {
        this.form = {
          id: found.id,
          mappingId: String(found.mappingId),
          procedures: found.procedures || [],
          insurances: found.insurances || [],
          synced: found.synced || false,
        };
        this.selected = true;

        if (!this.form.synced && this.form.procedures.length === 0) {
          this.setAlert('error', 'Nenhum procedimento mapeado');
        }
      }

      this.registerAblyListener();
    },
    registerAblyListener() {
      const { realtime } = AblyService.instance(this.user.id);
      const channel = realtime.channels.get(this.eventsChannel);
      channel.subscribe(this.syncEventListener);
      this.ablyUnsubscribe = () => channel.unsubscribe(this.syncEventListener);
    },
    syncEventListener({ name, data }) {
      if (name === 'finish' && data.professionalId === this.form.id) {
        this.syncing = false;
        this.form.synced = true;
        this.clearAlert();
      }
    },
    toggle() {
      if (!this.selected) {
        this.selected = true;
      } else {
        this.selected = false;
        this.dirty = !!this.form.mappingId;
      }
    },
    onMxmedProfessionalChange() {
      this.form.procedures = [];
      this.form.insurances = [];
      this.form.synced = false;
      this.dirty = true;
      this.clearAlert();
    },
    setAlert(type, message) {
      if (this.selected) {
        this.alert.type = type;
        this.alert.message = message;
        this.$emit('alert', { type, message });
      }
    },
    clearAlert() {
      this.alert.type = '';
      this.alert.message = '';
    },
    async save() {
      this.saving = true;
      try {
        let newMappings;
        if (this.selected) {
          newMappings = await api.mapping.saveProfessional(this.buildPayload());
        } else {
          newMappings = await api.mapping.removeProfessional(this.form.id);
          this.form = this.blankForm();
        }
        this.$emit('update-mappings', newMappings);
        this.dirty = false;
      } catch (e) {
        this.$toast.error(e);
      }
      this.saving = false;
    },
    buildPayload() {
      return {
        id: this.form.id,
        mappingId: String(this.form.mappingId),
        procedures: this.form.procedures.map(p => ({
          id: p.id,
          mappingId: String(p.mappingId),
        })),
        insurances: this.form.insurances.map(ins => ({
          id: ins.id,
          mappingId: String(ins.mappingId),
        })),
      };
    },
    async sync() {
      this.syncing = true;
      try {
        await api.mapping.syncProfessional(this.form.id);
      } catch (e) {
        this.syncing = false;
        this.$toast.error(e);
      }
    },
    openResourcesModal(type) {
      this.resourceModal.type = type;
      this.resourceModal.show = true;
    },
    onResourcesSave({ type, items }) {
      if (type === 'procedure') {
        this.form.procedures = items;
        if (items.length === 0) {
          this.setAlert('error', 'Nenhum procedimento mapeado');
        } else {
          this.clearAlert();
        }
      } else if (type === 'insurance') {
        this.form.insurances = items;
      }
      this.dirty = true;
      this.resourceModal.show = false;
    },
  },
};
</script>
